import React, { useState, useEffect } from 'react';
import { ApiService } from '../../services/ApiService';
import { Node, NodePosition } from '../../models/nodes/Node';
import { Form } from 'models/forms/Form';
import FormList from 'components/forms/FormList';
import { FormEnrollment } from 'models/enrollments/FormEnrollment';
import { utilities } from 'utils/utilities';
import NodeComponent from './NodeComponent';
import { Workflow } from 'models/workflows/Workflow';
import { useParams } from 'react-router-dom';

interface WorkflowCreatorProps {
  workflow: Workflow;
  ownerId: string;
}

// Used to create new workflows and edit them.
const WorkflowCreator: React.FC<WorkflowCreatorProps> = ({ workflow: initialWorkflow, ownerId }) => {
  const [forms, setForms] = useState<Form[]>([]);
  const [nodes, setNodes] = useState<Node[]>(initialWorkflow.nodes || []);
  const [workflow, setWorkflow] = useState<Workflow>(initialWorkflow);
  const [workflowName, setWorkflowName] = useState<string>(initialWorkflow.name ?? "");
  const [isActive, setIsActive] = useState<boolean>(initialWorkflow.isActive ?? false);
  const [workflowLink, setWorkflowLink] = useState<string>(initialWorkflow.workflowLink ?? "");
  const { teamId } = useParams<{ teamId: string; }>();

  if (!teamId) return null;

  const handleFormClick = (formId: string | Form) => {
    if (nodes.length === 1) {
      alert("Currently, only one stage is allowed. More to come!");
      return;
    }

    let form: Form;

    if (typeof formId === 'string') {
      throw new Error("formId should be an actual form in this case");
    } else {
      form = formId;
    }

    const newEnrollment: FormEnrollment = new FormEnrollment(formId.id, formId);
    const pos: NodePosition = NodePosition.Both; // TODO: Alter this when more nodes are allowed.
    const newNode: Node = new Node(newEnrollment, utilities.generateGUID(), pos);
    newNode.index = nodes.length === 0 ? 1 : nodes.length + 1;

    setNodes((prevNodes) => [...prevNodes, newNode]);
  };

  const handleDelete = (id: string) => {
    setNodes((prevNodes) => prevNodes.filter((obj) => obj.id !== id));
  }

  const handleUpdate = (node: Node) => {
    setNodes((prevNodes => {
      return prevNodes.map(n => n.id === node.id ? node : n);
    })
    );
  }

  const handleSaveWorkflowClick = () => {

    const isNewWorkflow = initialWorkflow.id === utilities.DEFAULT_GUID;
    const wf = new Workflow(false);
    wf.id = isNewWorkflow ? wf.id : initialWorkflow.id;
    wf.name = workflowName;
    wf.workflowLink = workflowLink;
    wf.isActive = isActive;
    wf.nodes = nodes;
    wf.description = "description here";

    console.log("Saving workflow:", wf);
    const response = isNewWorkflow
      ? ApiService.insertWorkflow(teamId, wf)
      : ApiService.updateWorkflow(teamId, wf);

    console.log(response);
  }

  const handleWorkflowNameChange = (value: string) => {
    setWorkflowName(value);
  }

  return (
    <div className="min-h-screen bg-gray-100 p-4">
      <div className="container mx-auto bg-white p-6 rounded-lg shadow-lg">
        <h1 className="text-2xl font-bold text-gray-800 mb-4">Workflow Creator</h1>
        <div className="mb-4">
          <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="workflowName">
            Workflow Name:
          </label>
          <input
            type="text"
            id="workflowName"
            value={workflowName}
            onChange={(e) => handleWorkflowNameChange(e.target.value)}
            className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
          />
        </div>
        <div className="mb-4">
          <label className="inline-flex items-center">
            <input
              type="checkbox"
              checked={isActive}
              onChange={() => setIsActive(!isActive)}
              className="form-checkbox h-5 w-5 text-blue-600"
            />
            <span className="ml-2 text-gray-700">Enable Workflow</span>
          </label>
        </div>
        <div className="mb-4">
          <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor='workflowLink'>
            Workflow Link:
            <input
              type="input"
              id='workflowLink'
              value={workflowLink}
              onChange={(e) => setWorkflowLink(e.target.value)}
              className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
            />
          </label>
        </div>
        {nodes.length > 0 && (
          <button
            onClick={handleSaveWorkflowClick}
            className="inline-flex items-center justify-center rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
          >
            Save Workflow
          </button>
        )}
        <div className="flex flex-col lg:flex-row gap-6 mt-6">
          <div className="flex-grow">
            <h2 className="text-xl font-semibold text-gray-800 mb-4">Stages</h2>
            <p className="text-gray-600 mb-4">Click on a form, queue, or calendar to set this as a stage. A stage is a step in your workflow process that a client will visit.</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {nodes.map((node) => (
                <div
                  key={node.id}
                  className="border border-gray-300 rounded-lg p-4 bg-white shadow-lg"
                >
                  <NodeComponent workflowId={workflow.id ?? utilities.DEFAULT_GUID} node={node} onDelete={handleDelete} onUpdate={handleUpdate} readonly={false} />
                </div>
              ))}
            </div>
          </div>
        </div>
        <div className="bg-gray-200 p-4 mt-6 rounded-lg">
          {forms && <FormList ownerId={ownerId} onCreateNew={() => { }} onSelect={handleFormClick} useDtos={false} formListCount={0} />}
        </div>
      </div>
    </div>
  );
};

export default WorkflowCreator;